import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import api from '../services/apiService';

const LwUGRNLogList = () => {
  const navigate = useNavigate();

  const [serverFilterOptions, setServerFilterOptions] = useState({
    vorgangsnummern: [],
    geraete: [],
    kunden: [],
    servicemitarbeiter: [],
    handling: [],
    sourceSystems: [],
    datesDatumUhrzeit: [],
  });

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('lwUGRNLogFilters');
    return saved
      ? JSON.parse(saved)
      : {
          vorgangsnummer: null,
          geraet: null,
          kunde: null,
          servicemitarbeiter: null,
          handling: null,
          sourceSystem: null,
          datumUhrzeit: null, // DD.MM.YYYY
          search: '',
          machineSwitch: null,
        };
  });

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 25;

  useEffect(() => {
    localStorage.setItem('lwUGRNLogFilters', JSON.stringify(filters));
    setCurrentPage(1);
  }, [filters]);

  const buildQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.append('limit', limit);
    p.append('page', currentPage);
    if (filters.vorgangsnummer)
      p.append('vorgangsnummer', filters.vorgangsnummer.value);
    if (filters.geraet) p.append('geraet', filters.geraet.value);
    if (filters.kunde) p.append('kunde', filters.kunde.value);
    if (filters.servicemitarbeiter)
      p.append('servicemitarbeiter', filters.servicemitarbeiter.value);
    if (filters.handling) p.append('handling', filters.handling.value);
    if (filters.sourceSystem)
      p.append('sourceSystem', filters.sourceSystem.value);
    if (filters.datumUhrzeit)
      p.append('datumUhrzeit', filters.datumUhrzeit.value);
    if (filters.search) p.append('search', filters.search);
     // 👇 NEU: Geratewechsel-Filter in Query hängen
    if (filters.machineSwitch) {
        p.append('machineSwitch', filters.machineSwitch.value); // "true" oder "false"
    }


    const s = p.toString();
    return s ? `?${s}` : '';
  }, [filters, currentPage]);

  // Öffnet die HTML-Zusammenstellung und springt direkt zum passenden Vorgang
  const openUgrnHtmlFromRow = (row, includeArchived = false) => {
    if (!row?.itemId) {
      alert(
        'Kein itemId am Datensatz vorhanden – Gerätezusammenstellung kann nicht geöffnet werden.'
      );
      return;
    }

    // Token wie bei Items ermitteln
    let token = '';
    try {
      const auth =
        api?.defaults?.headers?.common?.Authorization ||
        api?.defaults?.headers?.Authorization ||
        '';
      if (auth && auth.startsWith('Bearer ')) token = auth.slice(7).trim();
      if (!token) token = localStorage.getItem('token') || '';
      if (!token) token = sessionStorage.getItem('accessToken') || '';
    } catch {}

    if (!token) {
      alert('Kein Token gefunden. Bitte zuerst einloggen.');
      return;
    }

    const base =
      (api?.defaults?.baseURL || '').trim() ||
      (process.env.REACT_APP_BACKEND_URL || '').trim() ||
      window.location.origin;

    const hrefBase = base.replace(/\/+$/, '');
    const usp = new URLSearchParams();
    if (includeArchived) usp.set('includeArchived', '1');
    usp.set('token', token);
    usp.set('_ts', String(Date.now())); // Cache-Busting

    // Direkt zum Eintrag springen (#entry-{LOG_ID})
    const url = `${hrefBase}/lw-ugrn/by-item/${row.itemId}/html?${usp.toString()}#entry-${row.id}`;
    window.open(url, '_blank', 'noopener');
  };

  // Kleiner Helfer für Datumsformat dd.mm.yyyy
  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  // Optional – falls du später eine Detailseite baust
  const gotoDetail = (id) => navigate(`/lw-ugrn/${id}`);

    const gotoOrderEdit = (row) => {
    if (!row?.orderId) {
      alert('Kein Auftrag mit diesem UGRN-Eintrag verknüpft.');
      return;
    }
    navigate(`/orders/${row.orderId}/edit`);
  };


  const handleDelete = async (row) => {
    if (!window.confirm(`Eintrag #${row.id} wirklich löschen?`)) return;

    try {
      await api.delete(`/lw-ugrn/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      console.error('Fehler beim Löschen des Eintrags:', err);
      alert('Löschen fehlgeschlagen. Details in der Konsole.');
    }
  };

    const gotoOrderByNumber = (orderNo) => {
    if (!orderNo) return;

    // 👉 Hier ggf. an deine echte Order-Edit-Route anpassen
    // Falls deine Route z.B. /orders/edit/:orderNumber heißt, dann entsprechend ändern.
    navigate(`/orders/${encodeURIComponent(orderNo)}`);
  };


  // Filter-Optionen laden
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/lw-ugrn/filter-options');
        setServerFilterOptions(data || {});
      } catch (err) {
        console.error('Filter-Optionen Fehler (UGRN):', err);
      }
    })();
  }, []);

  // Liste laden
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lw-ugrn/list${buildQuery}`);
        setRows(data?.data || []);
        setTotalPages(data?.pages || 1);
      } catch (err) {
        console.error('LwUGRNLog list Fehler:', err);
      }
    })();
  }, [buildQuery]);

  return (
    <div className="lw-ugrn-log-list">
      <Helmet>
        <title>LW Übergaben & Rücknahmen</title>
        <meta
          name="description"
          content="Übergaben und Rücknahmen – Übersicht"
        />
      </Helmet>

      <h1>LW Übergaben & Rücknahmen</h1>

      {/* Filter */}
      <div style={{ marginBottom: 16 }}>
        <h3></h3>
        <div
          className="filter-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))',
            gap: 8,
          }}
        >
          <Select
            options={serverFilterOptions.vorgangsnummern?.map((v) => ({
              value: v,
              label: v,
            }))}
            placeholder="Vorgangsnummer"
            isClearable
            value={filters.vorgangsnummer}
            onChange={(sel) => setFilters({ ...filters, vorgangsnummer: sel })}
          />
          <Select
            options={serverFilterOptions.geraete?.map((v) => ({
              value: v,
              label: v,
            }))}
            placeholder="Gerät"
            isClearable
            value={filters.geraet}
            onChange={(sel) => setFilters({ ...filters, geraet: sel })}
          />
          <Select
            options={serverFilterOptions.kunden?.map((v) => ({
              value: v,
              label: v,
            }))}
            placeholder="Kunde"
            isClearable
            value={filters.kunde}
            onChange={(sel) => setFilters({ ...filters, kunde: sel })}
          />
          <Select
            options={serverFilterOptions.servicemitarbeiter?.map((v) => ({
              value: v,
              label: v,
            }))}
            placeholder="Servicemitarbeiter"
            isClearable
            value={filters.servicemitarbeiter}
            onChange={(sel) =>
              setFilters({ ...filters, servicemitarbeiter: sel })
            }
          />
          <Select
            options={serverFilterOptions.handling?.map((v) => ({
              value: v,
              label: v,
            }))}
            placeholder="Handling (UG / RN …)"
            isClearable
            value={filters.handling}
            onChange={(sel) => setFilters({ ...filters, handling: sel })}
          />
          <Select
            options={serverFilterOptions.sourceSystems?.map((v) => ({
              value: v,
              label: v,
            }))}
            placeholder="Source System"
            isClearable
            value={filters.sourceSystem}
            onChange={(sel) => setFilters({ ...filters, sourceSystem: sel })}
          />
          <Select
            options={serverFilterOptions.datesDatumUhrzeit?.map((v) => ({
              value: v,
              label: v,
            }))}
            placeholder="Datum"
            isClearable
            value={filters.datumUhrzeit}
            onChange={(sel) => setFilters({ ...filters, datumUhrzeit: sel })}
          />
            <Select
    placeholder="Gerätewechsel"
    isClearable
    value={filters.machineSwitch}
    onChange={(sel) => setFilters({ ...filters, machineSwitch: sel })}
    options={[
      { value: 'true',  label: 'Gerätewechsel: Ja' },
      { value: 'false', label: 'Gerätewechsel: Nein' },
    ]}
  />
          <input
            type="text"
            placeholder="Suche (Gerät, Vorgang, Kunde …)"
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value })
            }
            style={{
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
            }}
          />
        </div>
      </div>

           {/* Tabelle */}
      {rows?.length ? (
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Datum</th>
              <th>Order</th>
              <th>Handling</th>
              <th>Gerät</th>
              <th>LW-ID</th>
              <th>Kunde</th>
              <th>Servicemitarbeiter</th>
              <th>Source</th>
              <th>Gerätewechsel</th>
              <th>Vorgang</th>
              <th>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {[...rows]
              .sort(
                (a, b) =>
                  new Date(b.datumUhrzeit || b.createdAt) -
                  new Date(a.datumUhrzeit || a.createdAt)
              )
              .map((r) => {
                const handlingText =
                  (r.handling || '').toUpperCase() === 'UG'
                    ? 'Übergabe (UG)'
                    : (r.handling || '').toUpperCase() === 'RN'
                    ? 'Rücknahme (RN)'
                    : r.handling || '-';

                return (
                  <tr key={r.id}>
                    {/* ID */}
                    <td>{r.id}</td>

                    {/* Datum */}
                    <td>{fmtDate(r.datumUhrzeit)}</td>

                    {/* Order */}
                    <td
  onClick={() => r.orderId && gotoOrderEdit(r)}
  onKeyDown={(e) => {
    if ((e.key === 'Enter' || e.key === ' ') && r.orderId) {
      gotoOrderEdit(r);
    }
  }}
  role="button"
  tabIndex={0}
  title={
    r.orderId
      ? `Auftrag ${r.order || ''} öffnen`
      : 'Kein Auftrag verknüpft'
  }
  style={{
    color: r.orderId ? '#007bff' : '#666',
    textDecoration: r.orderId ? 'underline' : 'none',
    cursor: r.orderId ? 'pointer' : 'not-allowed',
  }}
>
  {r.order || '-'}
</td>



                    {/* Handling */}
                    <td>{handlingText}</td>

                    {/* Gerät (klickbar für HTML-Ansicht) */}
                    <td
                      onClick={() => r.itemId && openUgrnHtmlFromRow(r)}
                      onKeyDown={(e) => {
                        if (
                          (e.key === 'Enter' || e.key === ' ') &&
                          r.itemId
                        ) {
                          openUgrnHtmlFromRow(r);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      title={
                        r.itemId
                          ? 'Übergabe/Rücknahme-Historie anzeigen'
                          : 'Keine itemId vorhanden'
                      }
                      style={{
                        color: r.itemId ? '#007bff' : '#666',
                        textDecoration: r.itemId ? 'underline' : 'none',
                        cursor: r.itemId ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {r.geraet || '-'}
                    </td>

                    {/* LW-ID */}
                    <td>{r.lwId || '-'}</td>

                    {/* Kunde */}
                    <td>{r.kunde || '-'}</td>

                    {/* Servicemitarbeiter */}
                    <td>{r.servicemitarbeiter || '-'}</td>

                    {/* Source */}
                    <td>{r.sourceSystem || '-'}</td>

                    {/* Gerätewechsel */}
                    <td>
  {r.machineSwitch ? (
    <button
      type="button"
      style={{
        padding: '4px 10px',
        borderRadius: 999,
        border: '1px solid #cc0000',
        background: '#ff4d4d',
        color: '#fff',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'default',
      }}
      title="Gerätewechsel: Das übergebene Gerät gehört nicht zum Auftrag."
    >
      Ja
    </button>
  ) : (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        border: '1px solid #ccc',
        background: '#f3f3f3',
        color: '#555',
        fontSize: 11,
      }}
      title="Kein Gerätewechsel erkannt."
    >
      Nein
    </span>
  )}
</td>


                    {/* Vorgang */}
                    <td>{r.vorgangsnummer || '-'}</td>

                    {/* Aktion */}
                    <td>
                      <button
                        type="button"
                        onClick={() => handleDelete(r)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          border: '1px solid #cc0000',
                          background: '#ff4d4d',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        X
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      ) : (
        <p>Keine Einträge vorhanden.</p>
      )}


      {/* Pagination */}
      <div
        style={{
          marginTop: 16,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <button
          onClick={() => setCurrentPage((p) => (p > 1 ? p - 1 : 1))}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            background: '#9b9b9b',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
          }}
        >
          Vorherige
        </button>
        <span>
          Seite {currentPage} von {totalPages}
        </span>
        <button
          onClick={() =>
            setCurrentPage((p) => (p < totalPages ? p + 1 : p))
          }
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 12px',
            background: '#9b9b9b',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
          }}
        >
          Nächste
        </button>
      </div>
    </div>
  );
};

export default LwUGRNLogList;
